import { useCallback, useMemo } from "react";
import type { Dp } from "./CoState";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import UnitImage from "common/src/tft-static/UnitImage";
import { make as UnitName0 } from "common/src/tft-static/UnitName0.bs";
import { searchUnits } from "../common/Search2";
import { make as UnitTooltip } from "common/src/tft-static/UnitTooltip.bs";
import { use as useSet } from "common/src/data/TftSet.bs";
import InfoOutline from "@mui/icons-material/InfoOutlined";
import * as T from "common/src/bindings/Translation2";

const cols = [
  makeCol({
    width: 140,
    order: ([unitId], _, { unitsT }) => unitsT(unitId),
    defaultDesc: false,
    renderTitle: (_) => <> {T.common("Unit")} </>,
    renderValue: ([unitId, _1], { onExpand }, { unitsT }) => (
      <div
        className="flex items-center text-sm cursor-pointer"
        onClick={(_) =>
          onExpand({
            typ: "u",
            id: unitId,
            sid: unitId,
            starLevel: 0,
            label: unitsT(unitId),
          })
        }
      >
        <div className="flex-shrink-0">
          <UnitTooltip unit={unitId}>
            <UnitImage unitId={unitId} size={28} />
          </UnitTooltip>
        </div>
        <div className="pl-[6px] font-roboto font-normal truncate">
          <UnitName0 unit={unitId} />
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 75,
    // order: ([_1, c, v]) => v.count / c,
    order: ([_1, v]) => v.count,
    alignRight: true,
    renderTitle: (_) => <> {T.common("0 Opps")} </>,
    tooltip: ([_1, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 65,
    order: ([_1, v]) => v.delta,
    alignRight: true,
    renderTitle: (_) => <> {T.common("Delta")} </>,
    tooltip: ([_1, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_1, v]) => (
      <> {!v.delta ? "-" : (v.delta > 0 ? "+" : "") + v.delta.toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 75,
    order: ([_1, _2, v]) => v.count,
    // order: ([_1, c, _2, v]) => v.count / c,
    alignRight: true,
    renderTitle: (_) => <> {T.common("1 Opp")} </>,
    tooltip: ([_1, _2, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_1, _2, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 65,
    order: ([_1, _2, v]) => v.delta,
    alignRight: true,
    renderTitle: (_) => <> {T.common("Delta")} </>,
    tooltip: ([_1, _2, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_1, _2, v]) => (
      <> {!v.delta ? "-" : (v.delta > 0 ? "+" : "") + v.delta.toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 75,
    // order: ([_1, c, _2, _3, v]) => v.count / c,
    order: ([_1, _2, _3, v]) => v.count,
    alignRight: true,
    renderTitle: (_) => <> {T.common("2 Opps")} </>,
    tooltip: ([_1, _2, _3, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_1, _2, _3, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 65,
    order: ([_1, _2, _3, v]) => v.delta,
    alignRight: true,
    renderTitle: (_) => <> {T.common("Delta")} </>,
    tooltip: ([_1, _2, _3, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_1, _2, _3, v]) => (
      <> {!v.delta ? "-" : (v.delta > 0 ? "+" : "") + v.delta.toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 75,
    // order: ([_1, c, _2, _3, _4, v]) => v.count / c,
    order: ([_1, _2, _3, _4, v]) => v.count,
    alignRight: true,
    renderTitle: (_) => <> {T.common("3+ Opps")} </>,
    tooltip: ([_1, _2, _3, _4, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_1, _2, _3, _4, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 65,
    order: ([_1, _2, _3, _4, v]) => v.delta,
    alignRight: true,
    renderTitle: (_) => <> {T.common("Delta")} </>,
    tooltip: ([_1, _2, _3, _4, v]) => (
      <>
        {humanReadableCount(v.count) +
          " games " +
          (v.place / v.count).toFixed(2) +
          " avg. place"}
      </>
    ),
    renderValue: ([_1, _2, _3, _4, v]) => (
      <> {!v.delta ? "-" : (v.delta > 0 ? "+" : "") + v.delta.toFixed(2)} </>
    ),
  }),
];

type Props = {
  data: Array<[string, Dp, Dp, Dp, Dp]>;
  search: string;
  minSample: number;
  baseCount: number;
};

const CoCounters = ({ data, search, minSample, baseCount }: Props) => {
  const set = useSet();
  const keyFn = useCallback(([id, _]: [string, Dp]) => id, []);
  const context = useMemo(() => ({ baseCount }), [baseCount]);

  const visibleFilter = useMemo(() => {
    if (search === "") {
      return () => true;
    }
    const passIds = searchUnits(search, set);

    return ([unitId]: [string]) => passIds.has(unitId);
  }, [search]);

  const filteredData = useMemo(
    () => data,
    // data.filter(
    //   ([_, v0, v1, v2, v3]) =>
    //     v0.count + v1.count + v2.count + v3.count >= minSample,
    // ),
    [data, minSample],
  );

  let hasResults =
    search === "" || filteredData.some((v) => visibleFilter([v[0]]));

  return filteredData.length === 0 ? (
    <div className="px-5 py-6 text-xl text-medium text-white2">
      No unit combos with at least {minSample} games played
    </div>
  ) : (
    <>
      <div className="flex gap-2 items-center pt-3 pt-2 pb-3 px-4 leading-tight">
        <InfoOutline />
        {T.common("Counters tab explanation")}
      </div>
      <StatsTable
        cols={cols}
        data={filteredData}
        keyFn={keyFn}
        stickyColCount={1}
        defaultSortIdx={1}
        top="t262"
        context={context}
        visibleFilter={visibleFilter}
      />
      {hasResults ? null : (
        <div className="p-5 text-xl text-medium text-white2">
          No results found matching the search
        </div>
      )}
    </>
  );
};

export default CoCounters;
